import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { Login } from "./pages/login/Login";
import { PrivateRoute } from "./PrivateRoute";
import { RoleRoute } from "./RoleRoute";
import { AdminRoute } from "./AdminRoute";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import AdminUsuarios from "./pages/admin/AdminUsuarios";
import AdminInstituicoes from "./pages/admin/instituicoes/AdminInstituicoes";
import AdminInstituicaoDetalhe from "./pages/admin/instituicoes/AdminInstituicaoDetalhe";
import AdminInstituicaoUsuarios from "./pages/admin/instituicoes/AdminInstituicaoUsuarios";
import AdminInstituicaoTurmas from "./pages/admin/instituicoes/AdminInstituicaoTurmas";
import AdminInstituicaoCoordenacoes from "./pages/admin/instituicoes/AdminInstituicaoCoordenacoes";
import RedefinirSenha from "./pages/redefinirSenha/RedefinirSenha";
import ProblemasLogin from "./pages/problemasLogin/ProblemasLogin";
import PoliticaPrivacidade from "./pages/politicaPrivacidade/PoliticaPrivacidade";
import Layout from "./components/Layout";
import Dashboard from "./pages/dashboard/Dashboard";
import DashboardAluno from "./pages/dashboardAluno/DashboardAluno";
import CriarEvento from "./pages/criarEvento/CriarEvento";
import EventosPublicados from "./pages/eventosPublicados/EventosPublicados";
import EditarEvento from "./pages/editarEvento/EditarEvento";
import EditarTurma from "./pages/editarTurma/EditarTurma";
import EditarTurmaCoordenador from "./pages/editarTurmaCoordenador/EditarTurmaCoordenador";
import ListaAlunos from "./pages/listaAlunos/ListaAlunos";
import CriarTurma from "./pages/criarTurma/CriarTurma";
import VerCalendario from "./pages/verCalendario/VerCalendario";
import DetalheCalendario from "./pages/detalheCalendario/DetalheCalendario";
import DetalheCalendarioAluno from "./pages/detalheCalendarioAluno/DetalheCalendarioAluno";
import BaixarApp from "./pages/baixeApp/BaixarApp";
import SemTurma from "./pages/semTurma/SemTurma";
import TrocarSenhaObrigatoria from "./pages/trocarSenhaObrigatoria/TrocarSenhaObrigatoria";

// Detecta se usuário autenticado não possui nenhuma turma vinculada.
// O backend pode retornar `usuario.turmas` (array) ou `usuario.semVinculo` (bool).
// Por enquanto (Bloco 1), o backend não expõe isso, então retornamos false (assume com vinculo).
// TODO (Bloco 5): quando backend expor turmas[], usar: usuario?.turmas?.length === 0
function semTurmaVinculada(usuario) {
  if (!usuario) return false;

  // Forward-compatible: se backend adicionar o campo, usar ele
  if (usuario.semVinculo === true) return true;
  if (Array.isArray(usuario.turmas) && usuario.turmas.length === 0) return true;

  // Por enquanto, retorna false (não sabemos se tem ou não, assume que tem)
  return false;
}

function DashboardPage() {
  const { usuario } = useAuth();
  const tipo = String(usuario?.tipo || "").toLowerCase();

  if (tipo === "admin") return <Navigate to="/admin/instituicoes" replace />;

  // Coordenador não usa o dashboard de cards — vai direto para criar eventos
  if (tipo === "coordenador") return <Navigate to="/criar-evento" replace />;

  // Aluno sempre cai na DashboardAluno (com calendário), mesmo sem vínculo de turma.
  if (tipo === "aluno") return <DashboardAluno />;

  if (semTurmaVinculada(usuario)) return <SemTurma />;

  // Responsável não tem dashboard de cards — vai direto para criar eventos
  // (acesso normal: criar evento, eventos publicados, editar turma).
  if (tipo === "responsavel") return <Navigate to="/criar-evento" replace />;

  // professor → cards de turmas
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/problemas-login" element={<ProblemasLogin />} />
          {/* Página avulsa, exigida pelas lojas (Play Store / App Store).
              Proposital: nenhum link do app aponta para cá — só a URL direta. */}
          <Route
            path="/politica-de-privacidade"
            element={<PoliticaPrivacidade />}
          />

          <Route element={<PrivateRoute />}>
            <Route
              path="/trocar-senha-obrigatoria"
              element={<TrocarSenhaObrigatoria />}
            />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/sem-turma" element={<SemTurma />} />

              <Route element={<RoleRoute allowed={["aluno", "responsavel"]} />}>
                <Route
                  path="/detalhe-calendario-aluno/:data"
                  element={<DetalheCalendarioAluno />}
                />
              </Route>

              <Route
                element={
                  <RoleRoute
                    allowed={["professor", "coordenador", "responsavel"]}
                    allowIfResponsavelEmTurma
                  />
                }
              >
                <Route path="/criar-evento" element={<CriarEvento />} />
                <Route
                  path="/turma/:turmaId/alunos"
                  element={<ListaAlunos />}
                />
                <Route path="/eventos" element={<EventosPublicados />} />
                <Route path="/eventos/:id/editar" element={<EditarEvento />} />
                <Route
                  path="/editar-turma/:turmaId"
                  element={<EditarTurma />}
                />
              </Route>

              <Route element={<RoleRoute allowed={["coordenador"]} />}>
                <Route path="/nova-turma" element={<CriarTurma />} />
                <Route
                  path="/editar-turma-coordenador"
                  element={<EditarTurmaCoordenador />}
                />
                <Route path="/ver-calendario" element={<VerCalendario />} />
                <Route
                  path="/detalhe-calendario/:date"
                  element={<DetalheCalendario />}
                />
              </Route>

              <Route element={<AdminRoute />}>
                <Route path="/admin/usuarios" element={<AdminUsuarios />} />
                <Route
                  path="/admin/instituicoes"
                  element={<AdminInstituicoes />}
                />
                <Route
                  path="/admin/instituicoes/:id"
                  element={<AdminInstituicaoDetalhe />}
                >
                  <Route index element={<Navigate to="usuarios" replace />} />
                  <Route
                    path="usuarios"
                    element={<AdminInstituicaoUsuarios />}
                  />
                  <Route path="turmas" element={<AdminInstituicaoTurmas />} />
                  <Route
                    path="coordenacoes"
                    element={<AdminInstituicaoCoordenacoes />}
                  />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/baixar-app" element={<BaixarApp />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
